import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const PRESETS_MONTHLY = [15, 25, 50, 100];
const PRESETS_ONCE = [25, 50, 100, 250];
const MONTHLY_MIN = 15;

type Mode = "monthly" | "once";

export function DonateWidget({ compact = false }: { compact?: boolean }) {
  const [mode, setMode] = useState<Mode>("monthly");
  const [amount, setAmount] = useState<number>(25);
  const [custom, setCustom] = useState<string>("");
  const navigate = useNavigate();

  const presets = mode === "monthly" ? PRESETS_MONTHLY : PRESETS_ONCE;
  const finalAmount = custom ? Number(custom) : amount;
  const minAmount = mode === "monthly" ? MONTHLY_MIN : 1;
  const valid = finalAmount >= minAmount;

  const submit = () => {
    if (!valid) return;
    navigate(`/donate?amount=${finalAmount}&type=${mode === "monthly" ? "monthly" : "one_time"}`);
  };

  return (
    <div
      className={cn(
        "rounded-2xl bg-background/95 p-5 text-foreground shadow-soft backdrop-blur",
        compact ? "" : "md:p-7"
      )}
    >
      <div className="mb-4 inline-flex rounded-full bg-muted p-1">
        <button
          onClick={() => setMode("monthly")}
          className={cn(
            "rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
            mode === "monthly" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
          )}
        >
          Monthly
        </button>
        <button
          onClick={() => setMode("once")}
          className={cn(
            "rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
            mode === "once" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
          )}
        >
          Give once
        </button>
      </div>

      <p className="mb-3 text-sm text-muted-foreground">
        Select an amount to give {mode === "monthly" ? "per month" : "today"}
      </p>

      <div className="mb-3 grid grid-cols-4 gap-2">
        {presets.map((p) => (
          <button
            key={p}
            onClick={() => {
              setAmount(p);
              setCustom("");
            }}
            className={cn(
              "rounded-lg border-2 px-2 py-3 text-center font-display font-semibold transition-all",
              !custom && amount === p
                ? "border-primary bg-primary/5 text-primary"
                : "border-border hover:border-primary/40"
            )}
          >
            ${p}
            {mode === "monthly" && <span className="block text-[10px] font-normal text-muted-foreground">/mo</span>}
          </button>
        ))}
      </div>

      <div className="relative mb-4">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
        <input
          inputMode="numeric"
          placeholder="Other amount"
          value={custom}
          onChange={(e) => setCustom(e.target.value.replace(/[^0-9]/g, ""))}
          className="w-full rounded-lg border-2 border-border bg-background py-3 pl-7 pr-3 font-display text-lg outline-none focus:border-primary"
        />
      </div>

      {mode === "monthly" && (
        <p className="mb-3 text-center text-[11px] text-muted-foreground">
          Monthly gifts start at ${MONTHLY_MIN}/month.
        </p>
      )}

      <Button onClick={submit} disabled={!valid} size="lg" className="w-full rounded-full text-base font-semibold">
        Donate {finalAmount ? `$${finalAmount}` : ""} {mode === "monthly" ? "/ month" : ""}
      </Button>

      <p className="mt-3 text-center text-xs text-muted-foreground">
        100% of your donation funds projects in Sri Lanka.
      </p>
    </div>
  );
}
